import type { InvestorSignupSupportSignalRequest } from '@/api/custom';

export const INVESTOR_SIGNUP_COMPLETION_FAILED_KIND = 'INVESTOR_SIGNUP_COMPLETION_FAILED';
export const INVESTOR_SIGNUP_COMPLETION_ERROR_KIND = 'INVESTOR_SIGNUP_COMPLETION_ERROR';

type ErrorLike = {
  code?: unknown;
  name?: unknown;
  message?: unknown;
  status?: unknown;
  response?: {
    status?: unknown;
  };
};

type BuildInvestorSignupSupportSignalRequestOptions = {
  token: string;
  error?: unknown;
  request_id?: string;
  completion_attempt_id?: string;
  normalized_error_fingerprint?: string;
  sentry_event_id?: string;
  sentry_issue_id?: string;
};

type ConfirmationResultLike = {
  kind?: string;
  message?: string;
};

const POST_CONFIRMATION_FAILURE_MARKER = 'postconfirmation invocation failed';

const sanitizeFingerprintPart = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80);

const hashText = (value: string): string => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) >>> 0;
  }
  return hash.toString(36);
};

const createCompletionAttemptId = (): string => {
  const randomUUID = globalThis.crypto?.randomUUID;
  if (typeof randomUUID === 'function') {
    return randomUUID.call(globalThis.crypto);
  }

  return `attempt-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

const getErrorMessage = (error: unknown): string => {
  if (typeof error === 'string') {
    return error;
  }
  if (error instanceof Error) {
    return error.message;
  }
  if (error && typeof error === 'object') {
    const maybeError = error as ErrorLike;
    if (typeof maybeError.message === 'string') {
      return maybeError.message;
    }
  }
  return '';
};

const getErrorCode = (error: unknown): string | null => {
  if (!error || typeof error !== 'object') {
    return null;
  }

  const maybeError = error as ErrorLike;
  if (typeof maybeError.code === 'string' && maybeError.code.trim()) {
    return maybeError.code;
  }
  if (typeof maybeError.name === 'string' && maybeError.name.trim()) {
    return maybeError.name;
  }
  if (typeof maybeError.status === 'number') {
    return `http_${maybeError.status}`;
  }
  if (typeof maybeError.response?.status === 'number') {
    return `http_${maybeError.response.status}`;
  }

  return null;
};

export const normalizeInvestorSignupCompletionErrorFingerprint = (error: unknown): string => {
  const message = getErrorMessage(error);
  const normalizedMessage = message.toLowerCase();

  if (normalizedMessage.includes('socket timeout')) {
    return 'investor_signup_completion:post_confirmation_socket_timeout';
  }
  if (normalizedMessage.includes(POST_CONFIRMATION_FAILURE_MARKER)) {
    return 'investor_signup_completion:post_confirmation_unknown';
  }
  if (normalizedMessage.includes('upstream service contract failure')) {
    return 'investor_signup_completion:upstream_contract_failure';
  }
  if (normalizedMessage.includes('token not found or invalid')) {
    return 'investor_signup_completion:invalid_invite_token';
  }

  const statusMatch = normalizedMessage.match(/status\s+(\d{3})/);
  if (statusMatch?.[1]) {
    return `investor_signup_completion:http_${statusMatch[1]}`;
  }

  const errorCode = getErrorCode(error);
  if (errorCode) {
    return `investor_signup_completion:${sanitizeFingerprintPart(errorCode)}`;
  }

  if (message) {
    return `investor_signup_completion:unknown_${hashText(message)}`;
  }

  return 'investor_signup_completion:unknown';
};

export const buildInvestorSignupSupportSignalRequest = ({
  token,
  error,
  request_id,
  completion_attempt_id,
  normalized_error_fingerprint,
  sentry_event_id,
  sentry_issue_id,
}: BuildInvestorSignupSupportSignalRequestOptions): InvestorSignupSupportSignalRequest => ({
  token,
  request_id,
  completion_attempt_id: completion_attempt_id || createCompletionAttemptId(),
  normalized_error_fingerprint:
    normalized_error_fingerprint || normalizeInvestorSignupCompletionErrorFingerprint(error),
  sentry_event_id,
  sentry_issue_id,
});

export const isInvestorSignupCompletionFailureMessage = (message?: string | null): boolean =>
  Boolean(message?.toLowerCase().includes(POST_CONFIRMATION_FAILURE_MARKER));

export const isInvestorSignupCompletionFailureResult = (result: ConfirmationResultLike): boolean =>
  result.kind === INVESTOR_SIGNUP_COMPLETION_FAILED_KIND ||
  isInvestorSignupCompletionFailureMessage(result.message);

export const isInvestorSignupCompletionErrorResult = (result: ConfirmationResultLike): boolean =>
  result.kind === INVESTOR_SIGNUP_COMPLETION_ERROR_KIND;
